import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'
import { getCurrentLocation, startLocationBroadcast, calculateBearing } from '../utils/gps'
import 'leaflet/dist/leaflet.css'
import 'leaflet-rotate'
import { initializeMapLayers } from './MapLayers'
import { getDeviceSensorsManager } from '../utils/device-sensors'
import TreasureModal from './TreasureModal'

/**
 * Map2 - Minimal, clean implementation
 * Phase 1: Leaflet + Rotate + GPS
 * Phase 2: Add eggs and gaming logic
 */

/**
 * Copy for the proximity status line.
 * @param {Function} t - i18next translate function
 * @param {Object|null} status - Latest 'proximitystatus' detail
 * @returns {string} Text to show the player
 */
function proximityStatusText(t, status) {
  if (!status) return t('proximity.searching')

  switch (status.reason) {
    case 'arrived':
      return t('proximity.arrived')
    case 'poor-accuracy':
      return t('proximity.poorSignal')
    case 'no-target':
      return t('proximity.searching')
    default:
      return t('proximity.goCloser', { distance: Math.round(status.distance) })
  }
}

export default function Map2({ treasure, foundTreasures, onTreasureTap, unlockMode = 'keyword' }) {
  const { t } = useTranslation()
  const isProximity = unlockMode === 'proximity'
  const [distanceToTreasure, setDistanceToTreasure] = useState(null)
  const [proximityStatus, setProximityStatus] = useState(null)
  const [showTreasureModal, setShowTreasureModal] = useState(false)
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(false)
  const [huntType, setHuntType] = useState('easter')
  const mapRef = useRef(null)
  const containerRef = useRef(null)
  const userMarkerRef = useRef(null)
  const accuracyCircleRef = useRef(null)
  const travelConeRef = useRef(null)
  const locationHistoryRef = useRef([])
  const treasureMarkersRef = useRef({}) // Store treasure markers by ID

  const stateRef = useRef({
    userLocation: null,
    deviceHeading: 0,
    travelHeading: null,
    currentAccuracy: Infinity
  })

  // Initialize map on mount
  useEffect(() => {
    if (mapRef.current) return

    const mapOptions = {
      dragging: true,
      touchZoom: true,
      doubleClickZoom: true,
      scrollWheelZoom: true,
      boxZoom: false,
      keyboard: false,
      maxZoom: 24,
      rotate: true // Enable rotation if leaflet-rotate is available
    }

    mapRef.current = L.map(containerRef.current.querySelector('[data-map]'), mapOptions)
      .setView([51.505, -0.09], 13)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 24
    }).addTo(mapRef.current)

    // Initialize backyard overlay layer
    initializeMapLayers(mapRef.current)

    // Get initial location
    const initLocation = async () => {
      try {
        const location = await getCurrentLocation()
        if (location) {
          stateRef.current.userLocation = location
          mapRef.current.setView([location.lat, location.lng], 13)

          // Auto-zoom to fit user + first treasure on load
          if (treasure) {
            const bounds = L.latLngBounds(
              [location.lat, location.lng],
              [treasure.lat, treasure.lng]
            )

            // Add padding
            const latDiff = bounds.getNorthEast().lat - bounds.getSouthWest().lat
            const lngDiff = bounds.getNorthEast().lng - bounds.getSouthWest().lng
            const expand = Math.max(latDiff, lngDiff) * 0.5

            bounds.extend([bounds.getNorthEast().lat + expand, bounds.getNorthEast().lng + expand])
            bounds.extend([bounds.getSouthWest().lat - expand, bounds.getSouthWest().lng - expand])

            mapRef.current.fitBounds(bounds, {
              padding: [60, 60],
              animate: true,
              duration: 0.5
            })
          }

        }
      } catch (err) {
        console.error('Location error:', err)
      }
    }

    initLocation()

    // Start the shared GPS broadcast. The 'gpsupdate' listener below does the
    // per-fix work, so it stays in sync with the current treasure. Deliberately
    // not stopped on unmount: this effect early-returns when the map already
    // exists, so a StrictMode mount/unmount/mount would kill the watch for good.
    startLocationBroadcast()

    // Hook into rotate button to toggle auto-rotation
    const setupRotateButton = () => {
      const rotateButton = document.querySelector('.leaflet-control-rotate-toggle')
      if (rotateButton) {
        // Remove any existing listeners first
        const newButton = rotateButton.cloneNode(true)
        rotateButton.parentNode.replaceChild(newButton, rotateButton)

        newButton.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          setAutoRotateEnabled(prev => {
            // If turning off, reset map to north-up (bearing 0)
            if (prev && mapRef.current && mapRef.current.setBearing) {
              mapRef.current.setBearing(0)
            }
            return !prev
          })
        })
      }
    }

    setTimeout(setupRotateButton, 500)
  }, [])

  // Listen to device heading and rotate map
  useEffect(() => {
    const handleHeadingEvent = (event) => {
      const heading = event.detail.heading
      stateRef.current.deviceHeading = heading

      // Only rotate if auto-rotate is enabled
      if (autoRotateEnabled && mapRef.current && mapRef.current.setBearing) {
        mapRef.current.setBearing(-heading)
      }
    }

    window.addEventListener('deviceheading', handleHeadingEvent)
    return () => window.removeEventListener('deviceheading', handleHeadingEvent)
  }, [autoRotateEnabled])

  // Render current treasure marker
  useEffect(() => {
    if (!treasure || !mapRef.current) return

    // Get hunt type from window data or props
    const hunt = window.__hunt || {}
    setHuntType(hunt.huntType || 'easter')

    // Remove old treasure marker if it exists
    if (treasureMarkersRef.current[treasure.id]) {
      mapRef.current.removeLayer(treasureMarkersRef.current[treasure.id])
    }

    // Check if current treasure has been found
    const isFound = foundTreasures.some(t => t.id === treasure.id)

    // Create emoji based on found status
    let emoji
    if (isFound) {
      emoji = hunt.huntType === 'birthday' ? '🎉' : '🐥'
    } else {
      emoji = hunt.huntType === 'birthday' ? '🎁' : '🥚'
    }

    const icon = L.divIcon({
      html: `<div style="display: flex; align-items: center; justify-content: center; font-size: 18px; width: 25px; height: 25px; font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif; line-height: 1; background: transparent;">${emoji}</div>`,
      iconSize: [25, 25],
      iconAnchor: [12, 12],
      className: 'treasure-marker'
    })

    treasureMarkersRef.current[treasure.id] = L.marker(
      [treasure.lat, treasure.lng],
      { icon }
    )
      .addTo(mapRef.current)
      .on('click', () => {
        // Proximity hunts unlock by walking there — nothing to type.
        if (isProximity) return
        setShowTreasureModal(true)
      })

    // Zoom to fit user + treasure
    if (stateRef.current.userLocation) {
      const bounds = L.latLngBounds(
        [stateRef.current.userLocation.lat, stateRef.current.userLocation.lng],
        [treasure.lat, treasure.lng]
      )

      // Add padding
      const latDiff = bounds.getNorthEast().lat - bounds.getSouthWest().lat
      const lngDiff = bounds.getNorthEast().lng - bounds.getSouthWest().lng
      const expand = Math.max(latDiff, lngDiff) * 0.5

      bounds.extend([bounds.getNorthEast().lat + expand, bounds.getNorthEast().lng + expand])
      bounds.extend([bounds.getSouthWest().lat - expand, bounds.getSouthWest().lng - expand])

      mapRef.current.fitBounds(bounds, {
        padding: [60, 60],
        animate: true,
        duration: 0.5
      })
    }
  }, [treasure?.id, foundTreasures, onTreasureTap])

  // Render found treasure markers (chickens or surprises)
  useEffect(() => {
    if (!foundTreasures || !mapRef.current) return

    const hunt = window.__hunt || {}
    const foundEmoji = hunt.huntType === 'birthday' ? '🎉' : '🐥'

    // Remove markers for treasures no longer in foundTreasures list
    Object.entries(treasureMarkersRef.current).forEach(([id, marker]) => {
      if (!foundTreasures.find(t => t.id === id) && id !== treasure?.id) {
        mapRef.current.removeLayer(marker)
        delete treasureMarkersRef.current[id]
      }
    })

    // Add/update found treasure markers (excluding current treasure)
    foundTreasures.forEach(found => {
      if (found.id === treasure?.id) return // Current treasure handled in its own effect

      if (treasureMarkersRef.current[found.id]) {
        mapRef.current.removeLayer(treasureMarkersRef.current[found.id])
      }

      const chickenIcon = L.divIcon({
        html: `<div style="display: flex; align-items: center; justify-content: center; font-size: 18px; width: 25px; height: 25px; font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif; line-height: 1; background: transparent;">${foundEmoji}</div>`,
        iconSize: [25, 25],
        iconAnchor: [12, 12],
        className: 'treasure-marker'
      })

      treasureMarkersRef.current[found.id] = L.marker([found.lat, found.lng], { icon: chickenIcon })
        .addTo(mapRef.current)
    })
  }, [foundTreasures, treasure?.id])

  // Listen to the shared GPS broadcast. Re-subscribing when the treasure
  // changes keeps the distance readout pointed at the right target — a
  // callback registered once at mount would freeze on the first treasure.
  useEffect(() => {
    const handleGpsEvent = (event) => handleLocationUpdate(event.detail)

    window.addEventListener('gpsupdate', handleGpsEvent)
    return () => window.removeEventListener('gpsupdate', handleGpsEvent)
  }, [treasure?.id])

  // Show how close the player is in proximity hunts.
  useEffect(() => {
    if (!isProximity) {
      setProximityStatus(null)
      return
    }

    const handleStatus = (event) => setProximityStatus(event.detail)

    window.addEventListener('proximitystatus', handleStatus)
    return () => window.removeEventListener('proximitystatus', handleStatus)
  }, [isProximity])

  // Handle location updates
  const handleLocationUpdate = (newLocation) => {
    const state = stateRef.current
    state.userLocation = newLocation
    state.currentAccuracy = newLocation.accuracy || Infinity

    // Calculate travel heading from movement
    const history = locationHistoryRef.current
    history.push(newLocation)
    if (history.length > 5) history.shift()

    if (history.length >= 2) {
      const prev = history[history.length - 2]
      const curr = history[history.length - 1]
      state.travelHeading = calculateBearing(prev, curr)
    }

    // Calculate distance to current treasure
    if (treasure) {
      const distance = calculateDistance(newLocation, treasure)
      setDistanceToTreasure(distance)
    }

    // Update user location marker
    if (mapRef.current) {
      if (userMarkerRef.current) {
        userMarkerRef.current.setLatLng([newLocation.lat, newLocation.lng])
      } else {
        userMarkerRef.current = L.marker([newLocation.lat, newLocation.lng], {
          icon: createUserMarkerIcon()
        }).addTo(mapRef.current)
      }
    }

    // Update accuracy circle
    if (mapRef.current && newLocation.accuracy) {
      if (accuracyCircleRef.current) {
        accuracyCircleRef.current.setLatLng([newLocation.lat, newLocation.lng])
        accuracyCircleRef.current.setRadius(newLocation.accuracy)
      } else {
        accuracyCircleRef.current = L.circle(
          [newLocation.lat, newLocation.lng],
          {
            radius: newLocation.accuracy,
            fillColor: '#007BFF',
            color: '#0056b3',
            fillOpacity: 0.15,
            weight: 2,
            opacity: 0.6
          }
        ).addTo(mapRef.current)
      }
    }
  }

  // Calculate distance between two coordinates (in meters)
  const calculateDistance = (loc1, loc2) => {
    const R = 6371000 // Earth radius in meters
    const dLat = ((loc2.lat - loc1.lat) * Math.PI) / 180
    const dLng = ((loc2.lng - loc1.lng) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((loc1.lat * Math.PI) / 180) *
        Math.cos((loc2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Distance in meters
  }

  const createUserMarkerIcon = () => {
    return L.divIcon({
      html: `<div style="
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        width: 40px;
        height: 40px;
        font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
        line-height: 1;
        background: transparent;
      ">👣</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      className: 'user-location-marker'
    })
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div data-map style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }} />

        {/* Distance display */}
        {distanceToTreasure !== null && (
          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            background: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '4px',
            fontSize: '14px',
            zIndex: 1000,
            fontWeight: 'bold',
            pointerEvents: 'none'
          }}>
            📍 {distanceToTreasure < 1000 ? `${Math.round(distanceToTreasure)}m` : `${(distanceToTreasure / 1000).toFixed(1)}km`}
          </div>
        )}

        {/* Proximity status */}
        {isProximity && treasure && (
          <div style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            background: proximityStatus?.reason === 'arrived' ? 'rgba(40,167,69,0.9)' : 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '4px',
            fontSize: '14px',
            zIndex: 1000,
            fontWeight: 'bold',
            pointerEvents: 'none'
          }}>
            {proximityStatusText(t, proximityStatus)}
          </div>
        )}

        {/* Treasure Modal */}
        {showTreasureModal && treasure && (
          <TreasureModal treasure={treasure} onSubmit={onTreasureTap} onClose={() => setShowTreasureModal(false)} />
        )}
      </div>
    </div>
  )
}
